import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

interface NewBadgeProps {
  /** Badge text. Defaults to "NEW". */
  label?: string;
  /**
   * Placement is the host's job — pass absolute offsets to hang the ribbon over
   * a corner. The host must supply the positioning context (`position:
   * relative`) and must not clip it with `overflow: hidden`.
   */
  sx?: SxProps<Theme>;
}

/**
 * Placement for a badge hanging over the top-left corner of an MUI `Tab`.
 *
 * A tab is only ~48px tall with its label centred, leaving ~14px of clear space
 * above the text — less than the default ribbon occupies once rotated. So this
 * preset shrinks the ribbon and pins it into the tab's top-left padding, where
 * it clears the label instead of sitting on it.
 *
 * The host `Tab` must carry `position: relative` (the anchor) and
 * `overflow: visible`.
 */
export const TAB_NEW_BADGE_SX = {
  position: 'absolute' as const,
  top: 2,
  left: 4,
  fontSize: 7,
  px: '4px',
  py: 0,
  letterSpacing: '0.06em',
  borderRadius: '3px',
  transform: 'rotate(-10deg)',
  '@keyframes newBadgePulse': {
    '0%, 100%': { transform: 'rotate(-10deg) scale(1)' },
    '50%': { transform: 'rotate(-10deg) scale(1.09)' },
  },
};

/**
 * Small red ribbon flagging a freshly shipped feature. Rendered as a tilted
 * banner meant to hang over the top-left corner of whatever it marks, so it
 * reads as an overlay rather than part of the control's own label.
 *
 * Deliberately self-contained (fixed colors, no theme tokens) so it stays
 * legible on top of the accent gradients it gets placed on.
 */
const NewBadge: React.FC<NewBadgeProps> = ({ label = 'NEW', sx }) => (
  <Box
    component="span"
    aria-label={`${label} feature`}
    sx={[
      {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 0.7,
        py: '2px',
        borderRadius: '4px',
        background: 'linear-gradient(135deg, #ff5f52 0%, #f44336 55%, #c62828 100%)',
        color: '#fff',
        fontSize: 8.5,
        fontWeight: 900,
        lineHeight: 1.25,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        border: '1px solid rgba(255, 255, 255, 0.75)',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.45)',
        transform: 'rotate(-14deg)',
        transformOrigin: 'center',
        pointerEvents: 'none',
        animation: 'newBadgePulse 1.8s ease-in-out infinite',
        '@keyframes newBadgePulse': {
          '0%, 100%': { transform: 'rotate(-14deg) scale(1)' },
          '50%': { transform: 'rotate(-14deg) scale(1.09)' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {label}
  </Box>
);

export default NewBadge;
